import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Manages throttling state across the application to coordinate API request patterns.
 * When throttling is detected (429 errors), this manager switches execution strategy
 * from parallel batches to sequential requests to avoid overwhelming rate limits.
 */
@Injectable({
    providedIn: 'root'
})
export class ThrottleStateManager {
    private readonly _isThrottled = new BehaviorSubject<boolean>(false);
    private readonly _throttleCount = new BehaviorSubject<number>(0);
    private _throttleTimer: ReturnType<typeof setTimeout> | undefined;
    private readonly _throttleWindowMs = 60000; // 60 seconds

    public constructor() {}

    /**
     * Reports that a 429 throttling response was received.
     * Increments throttle count and sets throttled state to true.
     */
    public reportThrottle(): void {
        const currentCount = this._throttleCount.value + 1;
        this._throttleCount.next(currentCount);
        
        if (!this._isThrottled.value) {
            this._isThrottled.next(true);
            console.warn(`[ThrottleStateManager] Throttling detected. Switching to sequential execution mode.`);
        }

        // Reset the timer - keep throttled state active as long as we keep seeing 429s
        this._resetThrottleTimer();
    }

    /**
     * Reports that a successful response (non-429) was received.
     * Decrements throttle count and clears throttled state if no recent throttles.
     */
    public reportSuccess(): void {
        const currentCount = Math.max(0, this._throttleCount.value - 1);
        this._throttleCount.next(currentCount);

        // If we haven't seen throttles recently and count is back to zero, clear the state
        if (currentCount === 0 && !this._throttleTimer) {
            this._clearThrottledState();
        }
    }

    /**
     * Observable that emits true when throttled, false when not throttled.
     */
    public get isThrottled$(): Observable<boolean> {
        return this._isThrottled.asObservable();
    }

    /**
     * Synchronous check if currently in throttled state.
     */
    public get isThrottled(): boolean {
        return this._isThrottled.value;
    }

    /**
     * Resets all throttle state. Useful for testing or explicit state clearing.
     */
    public reset(): void {
        if (this._throttleTimer) {
            clearTimeout(this._throttleTimer);
            this._throttleTimer = undefined;
        }
        this._isThrottled.next(false);
        this._throttleCount.next(0);
    }

    /**
     * Reset the throttle timer. After the window expires without new throttles,
     * the throttled state is cleared.
     */
    private _resetThrottleTimer(): void {
        if (this._throttleTimer) {
            clearTimeout(this._throttleTimer);
        }

        this._throttleTimer = setTimeout(() => {
            this._throttleTimer = undefined;
            // If count is zero and no new throttles in the window, clear state
            if (this._throttleCount.value === 0) {
                this._clearThrottledState();
            }
        }, this._throttleWindowMs);
    }

    /**
     * Clears the throttled state and resets counters.
     */
    private _clearThrottledState(): void {
        if (this._isThrottled.value) {
            // eslint-disable-next-line no-console
            console.log(`[ThrottleStateManager] Throttling cleared. Resuming parallel execution mode.`);
            this._isThrottled.next(false);
        }
        this._throttleCount.next(0);
    }
}
